import React, { createContext, useContext, useState, useCallback } from "react";
import Slide from "@mui/material/Slide";
import Alert, { AlertColor } from "@mui/material/Alert";

import StackComponent from "../components/MUIcomponents/StackComponent/StackComponent";
import TypographyComponent from "../components/MUIcomponents/TypographyComponent/TypographyComponent";

interface AlertMessage {
  id: number;
  message: string;
  severity: AlertColor;
}

interface AlertContextType {
  showAlert: (
    message: string,
    severity?: AlertColor,
    timeout?: number | null,
  ) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [alerts, setAlerts] = useState<AlertMessage[]>([]);

  const showAlert = useCallback(
    (
      message: string,
      severity: AlertColor = "success",
      timeout: number | null = 4000,
    ) => {
      const id = new Date().getTime();

      setAlerts((prev) => [...prev, { id, message, severity }]);

      if (timeout !== null) {
        setTimeout(() => {
          setAlerts((prev) => prev.filter((alert) => alert.id !== id));
        }, timeout);
      }
    },
    [],
  );

  const removeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}

      <StackComponent
        spacing={2}
        sx={{
          position: "fixed",
          bottom: 24,
          left: 24,
          zIndex: 9999,
          maxWidth: "400px",
        }}
      >
        {alerts.map((alert) => (
          <Slide
            direction="right"
            in={true}
            mountOnEnter
            unmountOnExit
            key={alert.id}
          >
            <Alert
              variant="filled"
              severity={alert.severity}
              onClose={() => removeAlert(alert.id)}
              sx={{
                boxShadow: 3,
                color: "#fff",
                "& .MuiAlert-message": { padding: "8px 0" },
              }}
            >
              <TypographyComponent color="white">
                {alert.message}
              </TypographyComponent>
            </Alert>
          </Slide>
        ))}
      </StackComponent>
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert debe ser usado dentro de un AlertProvider");
  }
  return context;
};
